import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Disc3, Music2, Users, ListMusic, Video, Newspaper } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import VerifiedBadge from "../components/VerifiedBadge";
import SmartImage from "../components/SmartImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { key: "all", label: "Tudo" },
  { key: "articles", label: "Notícias" },
  { key: "artists", label: "Artistas" },
  { key: "cds", label: "Álbuns" },
  { key: "musics", label: "Músicas" },
  { key: "playlists", label: "Playlists" },
  { key: "videos", label: "Vídeos" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") || "";
  const [q, setQ] = useState(initial);
  const [tab, setTab] = useState("all");
  const [data, setData] = useState({ articles: [], artists: [], cds: [], musics: [], playlists: [], videos: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!initial || initial.length < 2) return;
    setLoading(true);
    fetch(`${API}/search?q=${encodeURIComponent(initial)}&limit=24`)
      .then((r) => r.json())
      .then(setData)
      .finally(() => setLoading(false));
  }, [initial]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    setParams({ q: q.trim() });
  };

  const total = (data.articles?.length || 0) + (data.artists?.length || 0) + (data.cds?.length || 0) + (data.musics?.length || 0) + (data.playlists?.length || 0) + (data.videos?.length || 0);

  const showSection = (key) => tab === "all" || tab === key;

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main className="pt-24 pb-16 px-4 sm:px-8 max-w-7xl mx-auto">
        <h1 className="font-heading text-3xl sm:text-4xl font-black mb-6 flex items-center gap-2">
          <Search size={28} className="text-[#FF007F]" /> Buscar
        </h1>
        <form onSubmit={onSubmit} className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="O que você quer ouvir? (artista, música, álbum...)"
            data-testid="search-input"
            className="w-full bg-zinc-900 border border-white/10 rounded-full pl-12 pr-4 py-4 focus:border-[#FF007F] focus:outline-none"
          />
        </form>

        {initial && (
          <>
            <div className="flex gap-2 mb-8 overflow-x-auto no-scrollbar">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition ${
                    tab === t.key ? "bg-[#FF007F] text-white" : "bg-zinc-900 text-zinc-400 hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {loading ? (
              <p className="text-zinc-500">Carregando...</p>
            ) : total === 0 ? (
              <p className="text-zinc-500 py-12 text-center">Nenhum resultado para "{initial}".</p>
            ) : (
              <div className="space-y-12">
                {showSection("articles") && data.articles?.length > 0 && (
                  <Section title="Notícias" icon={<Newspaper size={20} />}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {data.articles.slice(0, 12).map((a) => (
                        <Link
                          key={a.id}
                          to={`/artigo/${a.id}`}
                          className="group block bg-zinc-900/60 border border-white/5 rounded-xl overflow-hidden hover:border-[#FF007F]/40 transition"
                          data-testid={`search-article-${a.id}`}
                        >
                          <SmartImage
                            src={a.image_url?.startsWith("http") ? a.image_url : (a.image_url ? `${process.env.REACT_APP_BACKEND_URL}${a.image_url}` : null)}
                            alt={a.title}
                            aspect="16/9"
                            rounded=""
                          />
                          <div className="p-3">
                            <span className="text-[10px] uppercase tracking-wider font-bold text-[#FF007F]">{a.category || "Notícia"}</span>
                            <p className="text-sm font-bold mt-1 line-clamp-2 group-hover:text-[#FF007F] transition">{a.title}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </Section>
                )}
                {showSection("artists") && data.artists?.length > 0 && (
                  <Section title="Artistas" icon={<Users size={20} />}>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-5">
                      {data.artists.map((a) => (
                        <Link key={a.id} to={`/artista/${a.slug}`} className="text-center group">
                          <div className="aspect-square rounded-full bg-zinc-900 overflow-hidden mb-2 ring-2 ring-transparent group-hover:ring-[#FF007F]/50">
                            {a.photo_url ? (
                              <img src={a.photo_url.startsWith("http") ? a.photo_url : `${process.env.REACT_APP_BACKEND_URL}${a.photo_url}`} alt={a.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-3xl font-black text-zinc-600">{a.name?.charAt(0)}</div>
                            )}
                          </div>
                          <p className="text-xs font-bold flex items-center justify-center gap-1 truncate">{a.name} {a.verified && <VerifiedBadge size={11} />}</p>
                        </Link>
                      ))}
                    </div>
                  </Section>
                )}
                {showSection("cds") && data.cds?.length > 0 && (
                  <Section title="Álbuns" icon={<Disc3 size={20} />}>
                    <Grid items={data.cds} render={(c) => (
                      <Link key={c.id} to={`/cd/${c.id}`} className="group block">
                        <Cover src={c.cover_url} alt={c.cd_title} fallback={<Disc3 size={36} />} />
                        <p className="text-sm font-bold mt-2 truncate">{c.cd_title}</p>
                        <p className="text-xs text-zinc-500 truncate">{c.artist_name}</p>
                      </Link>
                    )} />
                  </Section>
                )}
                {showSection("playlists") && data.playlists?.length > 0 && (
                  <Section title="Playlists" icon={<ListMusic size={20} />}>
                    <Grid items={data.playlists} render={(p) => (
                      <Link key={p.id} to={`/playlist/${p.id}`} className="group block">
                        <Cover src={p.cover_image} alt={p.name} fallback={<ListMusic size={36} />} />
                        <p className="text-sm font-bold mt-2 truncate">{p.name}</p>
                        <p className="text-xs text-zinc-500">{p.music_ids?.length || 0} músicas</p>
                      </Link>
                    )} />
                  </Section>
                )}
                {showSection("musics") && data.musics?.length > 0 && (
                  <Section title="Músicas" icon={<Music2 size={20} />}>
                    <ol className="divide-y divide-white/5 border border-white/10 rounded-2xl overflow-hidden">
                      {data.musics.slice(0, 20).map((m) => {
                        // Build artist slug from name to navigate to profile
                        const slug = (m.artist_name || "")
                          .toLowerCase()
                          .normalize("NFD")
                          .replace(/[\u0300-\u036f]/g, "")
                          .replace(/[^a-z0-9]+/g, "-")
                          .replace(/^-+|-+$/g, "");
                        return (
                          <li key={m.id} data-testid={`search-music-${m.id}`}>
                            <Link
                              to={slug ? `/artista/${slug}` : "/"}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition group"
                            >
                              {m.artist_image ? (
                                <img
                                  src={m.artist_image.startsWith("http") ? m.artist_image : `${process.env.REACT_APP_BACKEND_URL}${m.artist_image}`}
                                  alt=""
                                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                  loading="lazy"
                                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#FF007F]/30 to-[#00E5FF]/30 flex items-center justify-center flex-shrink-0">
                                  <Music2 size={20} className="text-white/40" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold truncate group-hover:text-[#FF007F] transition">{m.title}</p>
                                <p className="text-xs text-zinc-500 truncate">{m.artist_name}</p>
                              </div>
                            </Link>
                          </li>
                        );
                      })}
                    </ol>
                  </Section>
                )}
                {showSection("videos") && data.videos?.length > 0 && (
                  <Section title="Vídeos" icon={<Video size={20} />}>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                      {data.videos.map((v) => (
                        <Link key={v.id} to="/vitrine" className="block">
                          <div className="aspect-[9/16] bg-zinc-900 rounded-xl overflow-hidden">
                            {v.thumbnail_url ? <img src={v.thumbnail_url.startsWith("http") ? v.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${v.thumbnail_url}`} alt={v.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-zinc-700"><Video size={28}/></div>}
                          </div>
                          <p className="text-xs mt-2 truncate">{v.title}</p>
                        </Link>
                      ))}
                    </div>
                  </Section>
                )}
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section>
      <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-white">
        <span className="text-[#FF007F]">{icon}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

function Grid({ items, render }) {
  return <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">{items.slice(0, 12).map(render)}</div>;
}

function Cover({ src, alt, fallback }) {
  return (
    <div className="aspect-square overflow-hidden rounded-xl bg-zinc-900">
      {src ? (
        <img
          src={src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`}
          alt={alt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-zinc-700">{fallback}</div>
      )}
    </div>
  );
}
